import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, createSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { Select, Dropdown, Pagination, DatePicker } from "antd";
import { CaretDownOutlined } from "@ant-design/icons";
import { Bar } from "react-chartjs-2";
import {
  BACKEND_DATE_FORMAT,
  COLORS,
  DROPDOWNS,
  UI_DATE_FORMAT,
} from "../../../consts";
const { RangePicker } = DatePicker;

const BarChart = ({
  barData,
  currentMetric,
  metricType,
  startDate,
  endDate,
  setEndDate,
  setStartDate,
  page,
  setPage,
  numBars,
  setNumBars,
  dataLen,
  metricUnit,
}) => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [displayData, setDisplayData] = useState({
    labels: [],
    datasets: [
      {
        label: "",
        data: [],
        backgroundColor: [],
        borderWidth: 1,
      },
    ],
  });
  const [maxVal, setMaxVal] = useState(100);
  const chartRef = useRef(null);
  const defaultGraphSorting = { key: "dscVal" };

  const isDictionary = (variable) => {
    return (
      typeof variable === "object" &&
      variable !== null &&
      !Array.isArray(variable)
    );
  };
  const getMetricLabel = (metric) => {
    if (typeof metric === "string") {
      return metric;
    } else if (isDictionary(metric)) {
      return metric.label;
    }
    return "Unknown Metric";
  };

  // Init & sort data when barData changes
  useEffect(() => {
    const sortedData = [...barData].sort((a, b) => b.val - a.val);
    setData(sortedData);
    setPage(1);
  }, [barData, setPage]);

  // Update chart data when page or numBars or data changes
  useEffect(() => {
    const leftIdx = numBars * (page - 1);
    const rightIdx = Math.min(numBars * page, data.length);
    const dataSlice = data.slice(leftIdx, rightIdx);
    setDisplayData({
      labels: dataSlice.map((bar) => bar.label),
      datasets: [
        {
          label: getMetricLabel(currentMetric),
          data: dataSlice.map((bar) => bar.val),
          backgroundColor: COLORS.primaryBlue,
        },
      ],
    });
  }, [numBars, data, page, currentMetric]);

  // Update maxVal for chart scaling
  useEffect(() => {
    setMaxVal(
      Math.max(
        ...barData.map((bar) => bar.val).filter((val) => !isNaN(val)),
        0,
      ),
    );
  }, [barData]);

  const handleNumBarsChange = (newVal) => {
    setNumBars(newVal);
    setPage(1);
  };

  const handleDateChange = (date) => {
    if (!date || date.length !== 2) {
      setStartDate(null);
      setEndDate(null);
      return;
    }
    setStartDate(date[0]?.format(BACKEND_DATE_FORMAT) || null);
    setEndDate(date[1]?.format(BACKEND_DATE_FORMAT) || null);
  };

  // Sorting
  const handleSortChange = ({ key }) => {
    let sortedData = [];
    if (key === "dscVal") sortedData = [...data].sort((a, b) => b.val - a.val);
    else if (key === "ascVal")
      sortedData = [...data].sort((a, b) => a.val - b.val);
    else if (key === "ascName")
      sortedData = [...data].sort((a, b) => a.label.localeCompare(b.label));
    else if (key === "dscName")
      sortedData = [...data].sort((a, b) => b.label.localeCompare(a.label));
    setPage(1);
    setData([...sortedData]);
  };

  const getLabelFromIndex = useCallback(
    (idx) => data[numBars * (page - 1) + idx]?.label,
    [page, numBars, data],
  );

  // Chart.js v4 click event handling
  const options = useMemo(
    () => ({
      responsive: true,
      plugins: {
        legend: { display: false },
      },
      scales: {
        y: {
          beginAtZero: false,
          title: {
            display: true,
            text: `${getMetricLabel(currentMetric)} ${metricUnit}`,
            font: { family: "'museoSansBold', sans-serif", size: 16 },
          },
          max: Math.ceil(1.1 * maxVal),
          suggestedMin: 0,
        },
        x: {
          title: {
            display: true,
            text: "Plot Name",
            font: { family: "'museoSansBold', sans-serif", size: 16 },
          },
          ticks: {
            maxRotation: 45,
            callback: (value) => {
              const label = displayData.labels[value] || "";
              return label.length > 18 ? label.slice(0, 15) + "…" : label;
            },
          },
        },
      },
      onClick: (event, elements, chart) => {
        if (elements?.length) {
          const idx = elements[0].index;
          const label = getLabelFromIndex(idx);
          if (label) {
            navigate({
              pathname: "/plots",
              search: createSearchParams({ plot: label }).toString(),
            });
          }
        }
      },
    }),
    [
      currentMetric,
      metricUnit,
      maxVal,
      displayData.labels,
      getLabelFromIndex,
      navigate,
    ],
  );

  return (
    <div
      className="flex flex-col items-center mb-10 py-4 rounded-md hover:shadow-2xl transition-shadow duration-300 transform hover:scale-101"
      id="barchart"
    >
      <div id="barcontent" className="w-full max-w-[1120px]">
        <p className="w-full text-center font-IBMPlexSansBold text-2xl mb-5 heading">
          {`${getMetricLabel(currentMetric)} (${metricType === "sum" ? "Cumulative" : "Average"})`}
        </p>
        <div
          id="barcontrols"
          className="flex justify-between mb-5 sm:flex-col sm:items-center"
        >
          <div className="flex items-center sm:mb-2 bar">
            <p className="text-sm  pr-2 ">Number of Bars:</p>
            <Select
              value={numBars}
              className="max-w-fit"
              placeholder="Bars per page:"
              onChange={handleNumBarsChange}
              options={DROPDOWNS.BAR_LIMIT_DATA}
            />
          </div>
          <div className="flex">
            <RangePicker
              value={
                startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : []
              }
              className="mr-4 date"
              onChange={handleDateChange}
              format={UI_DATE_FORMAT}
            />
            <Dropdown
              className="sort"
              menu={{
                items: DROPDOWNS.BAR_SORT_DATA,
                selectable: true,
                defaultSelectedKeys: [defaultGraphSorting.key],
                onClick: handleSortChange,
              }}
              trigger={["click"]}
              placement="bottom"
            >
              <div className="flex items-center cursor-pointer">
                <p className="text-sm  pr-2 ">Sort</p>
                <CaretDownOutlined />
              </div>
            </Dropdown>
          </div>
        </div>
        <div className="flex justify-center mb-5">
          <Bar ref={chartRef} data={displayData} options={options} />
        </div>
        <div className="flex justify-center">
          <Pagination
            hideOnSinglePage={true}
            current={page}
            pageSize={numBars}
            total={dataLen}
            onChange={setPage}
          />
        </div>
      </div>
    </div>
  );
};

export default BarChart;
